import React, { useState, useCallback, useMemo, useRef } from 'react'
import { orderBy } from 'lodash'
import InfiniteScroll from 'react-infinite-scroller'
import EmbedTopicsApi from '../../api/embed/TopicsApi'
import TopicCard from '../topics/Card'
import CardLoadingAnimation from '../shared/CardLoadingAnimation'
import EmbedLayout, { useEmbedParams } from './EmbedLayout'
import type { TopicData, ApiResponse } from './types'

const PAGE_SIZE = 9

const EmbedTopics: React.FC = () => {
  const { maxItems, partnerIds } = useEmbedParams()
  const [items, setItems] = useState<TopicData[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadingRef = useRef(false)

  // Stable by value, not by identity -- see the note in EmbedConversations.tsx.
  // A fresh array from useEmbedParams() in these deps refetches forever.
  const partnerIdsKey = partnerIds.join(',')
  const stablePartnerIds = useMemo(
    () => (partnerIdsKey ? partnerIdsKey.split(',').map(Number) : []),
    [partnerIdsKey]
  )

  const handleLoadMore = useCallback(() => {
    if (loadingRef.current) return
    if (maxItems && items.length >= maxItems) {
      setHasMore(false)
      return
    }
    loadingRef.current = true
    setLoading(true)

    const offset = items.length

    EmbedTopicsApi.getAll({ limit: PAGE_SIZE, offset, partnerIds: stablePartnerIds })
      .then((res: ApiResponse<TopicData[]>) => {
        // Stops the scroller on a failed request -- see the note in
        // EmbedConversations.tsx. Without it a failing feed retries forever.
        if (!res.response?.ok) {
          setHasMore(false)
          return
        }
        const newItems = res.json || []
        if (newItems.length < PAGE_SIZE) setHasMore(false)

        setItems((prev) => {
          const existingIds = new Set(prev.map((t) => t.id))
          const unique = newItems.filter((t) => !existingIds.has(t.id))
          let merged = [...prev, ...unique]
          merged = orderBy(merged, ['rank', 'createdAt'], ['asc', 'asc'])
          if (maxItems) merged = merged.slice(0, maxItems)
          return merged
        })
      })
      .finally(() => {
        loadingRef.current = false
        setLoading(false)
      })
  }, [items.length, maxItems, stablePartnerIds])

  return (
    <EmbedLayout>
      <InfiniteScroll loadMore={handleLoadMore} hasMore={hasMore && !loading} threshold={1000}>
        {items.length === 0 && !loading ? (
          <div className="embed-empty">No topics available</div>
        ) : (
          <div className="row">
            {items.map((item) => (
              <TopicCard key={item.id} topic={item} />
            ))}
          </div>
        )}
        {loading && <CardLoadingAnimation />}
      </InfiniteScroll>
    </EmbedLayout>
  )
}

export default EmbedTopics
